"use client";

import { useEffect, useRef } from "react";
import { useSession } from "@/lib/auth-client";
import { usePostHog } from "posthog-js/react";

type SessionUser = {
  id: string;
  email: string;
  name?: string | null;
};

type SessionData = {
  user?: SessionUser | null;
};

export default function PostHogUserSync(): null {
  const { data } = useSession();
  const posthog = usePostHog();
  const lastIdentifiedId = useRef<string | null>(null);

  useEffect(() => {
    if (!posthog) {
      return;
    }

    const session = (data ?? undefined) as SessionData | undefined;
    const user = session?.user ?? null;
    const distinctId = user?.id ?? null;

    if (user && distinctId) {
      const nextId = distinctId.trim();
      if (!nextId) {
        return;
      }

      const alreadyIdentified = lastIdentifiedId.current === nextId;
      const personProperties = {
        email: user.email,
        name: user.name ?? undefined,
      } as const;

      if (!alreadyIdentified) {
        posthog.identify(nextId, personProperties);
        lastIdentifiedId.current = nextId;
      } else {
        posthog.setPersonProperties?.(personProperties);
      }
    } else if (lastIdentifiedId.current) {
      posthog.reset();
      lastIdentifiedId.current = null;
    }
  }, [data, posthog]);

  return null;
}
